import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  obtenerPrestamoPorId,
  recibirEquiposPrestamo
} from '../../api/common';
import Swal from 'sweetalert2';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Badge,
  Spinner,
  Alert
} from 'react-bootstrap';
import {
  FaCheckCircle,
  FaSignature,
  FaArrowLeft,
  FaSave
} from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';
//import { uploadFile } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';

const RecibirEquiposPrestamos = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const sigCanvas = useRef(null);

  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [prestamo, setPrestamo] = useState(null);
  const [equiposSeleccionados, setEquiposSeleccionados] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [firmaCargada, setFirmaCargada] = useState(false);

  useEffect(() => {
    cargarPrestamo();
  }, [id]);

  const cargarPrestamo = async () => {
    try {
      setLoading(true);
      const resp = await obtenerPrestamoPorId(id);
      console.log('Respuesta al obtener préstamo:', resp);

      if (resp.status === 200) {
        setPrestamo(resp.data.data);
        
        // Pre-seleccionar equipos que aún no han sido devueltos
        const equiposPendientes = resp.data.data.equipos
          .filter(eq => eq.estado !== 'devuelto')
          .map(eq => eq.patron._id || eq.patron);
        
        setEquiposSeleccionados(equiposPendientes);
      } else {
        Swal.fire('Error', 'No se pudo cargar el préstamo', 'error');
        //navigate('/prestamos');
      }
    } catch (error) {
      console.error('Error al cargar préstamo:', error);
      Swal.fire('Error', 'Ocurrió un error al cargar el préstamo', 'error');
      //navigate('/prestamos');
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionEquipo = (patronId) => {
    if (equiposSeleccionados.includes(patronId)) {
      setEquiposSeleccionados(equiposSeleccionados.filter(id => id !== patronId));
    } else {
      setEquiposSeleccionados([...equiposSeleccionados, patronId]);
    }
  };

  const handleSeleccionarTodos = () => {
    const equiposPendientes = prestamo.equipos
      .filter(eq => eq.estado !== 'devuelto')
      .map(eq => eq.patron._id || eq.patron);

    if (equiposSeleccionados.length === equiposPendientes.length) {
      setEquiposSeleccionados([]);
    } else {
      setEquiposSeleccionados(equiposPendientes);
    }
  };

  const limpiarFirma = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setFirmaCargada(false);
    }
  };

  const handleFirmaChange = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setFirmaCargada(true);
    }
  };

  const handleRecibirEquipos = async () => {
    // Validaciones
    if (equiposSeleccionados.length === 0) {
      Swal.fire('Validación', 'Debe seleccionar al menos un equipo', 'warning');
      return;
    }

    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      Swal.fire('Validación', 'Debe firmar para confirmar la recepción', 'warning');
      return;
    }

    try {
      setGuardando(true);

      // Convertir firma a blob y subir a Firebase
      const firmaDataURL = sigCanvas.current.toDataURL('image/png');
      const blob = await (await fetch(firmaDataURL)).blob();

      /*const firmaURL = await uploadFile(
        blob,
        `firmas/recepciones/${id}_${Date.now()}.png`
      );*/

      // Preparar datos de recepción
      const datosRecepcion = {
        equiposDevueltos: equiposSeleccionados,
        usuarioRecibe: userData.uid,
        nombreUsuarioRecibe: `${userData.nombre || ''} ${userData.apellido || ''}`,
        firma:null, //firmaURL,
        observaciones
      };

      const resp = await recibirEquiposPrestamo(id, datosRecepcion);

      if (resp.status === 200) {
        const esDevolucionCompleta = resp.data.estado === 'devuelto';
        
        Swal.fire({
          icon: 'success',
          title: '¡Equipos Recibidos!',
          text: esDevolucionCompleta
            ? 'Todos los equipos han sido devueltos'
            : `Se recibieron ${equiposSeleccionados.length} equipo(s). Quedan pendientes por devolver.`,
          confirmButtonText: 'Aceptar'
        }).then(() => {
          navigate('/prestamos');
        });
      } else {
        Swal.fire('Error', resp.message || 'No se pudo registrar la recepción', 'error');
      }
    } catch (error) {
      console.error('Error al recibir equipos:', error);
      Swal.fire('Error', 'Ocurrió un error al recibir los equipos', 'error');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando información...</p>
      </Container>
    );
  }

  if (!prestamo) {
    return (
      <Container className="py-5">
        <Alert variant="danger">No se pudo cargar la información del préstamo</Alert>
      </Container>
    );
  }

  const equiposPendientes = prestamo.equipos.filter(eq => eq.estado !== 'devuelto');
  const equiposDevueltos = prestamo.equipos.filter(eq => eq.estado === 'devuelto');

  return (
    <Container fluid className="py-4 w-100">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate('/prestamos')}
            className="mb-2"
          >
            <FaArrowLeft className="me-2" />
            Volver
          </Button>
          <h2 className="mb-0">
            <FaCheckCircle className="me-2 text-success" />
            Recibir Equipos Prestados
          </h2>
          <p className="text-muted">
            Acta: <strong>{prestamo.numeroActa}</strong> | OT: <strong>{prestamo.numeroOt}</strong>
          </p>
        </Col>
      </Row>

      <Row>
        {/* Formulario de recepción */}
        <Col lg={5}>
          <Card className="shadow-sm mb-4 w-100">
            <Card.Header className="bg-success text-white w-100">
              <h5 className="mb-0">Información de Recepción</h5>
            </Card.Header>
            <Card.Body className="w-100">
              <Form>
                {/* Usuario que recibe */}
                <Form.Group className="mb-3">
                  <Form.Label>Recibido por</Form.Label>
                  <Form.Control
                    type="text"
                    value={`${userData?.nombre || ''} ${userData?.apellido || ''}`}
                    disabled
                  />
                </Form.Group>

                {/* Equipos seleccionados */}
                <div className="mb-3">
                  <strong>Equipos a recibir:</strong>
                  <Badge bg="info" className="ms-2">
                    {equiposSeleccionados.length} de {equiposPendientes.length}
                  </Badge>
                </div>

                {/* Observaciones */}
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Estado de los equipos, novedades, etc..."
                  />
                </Form.Group>

                {/* Firma */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaSignature className="me-2" />
                    Firma de Recepción
                    <span className="text-danger"> *</span>
                  </Form.Label>
                  <div
                    className="border rounded p-2"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        width: 400,
                        height: 150,
                        className: 'signature-canvas',
                        style: {
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          width: '100%',
                          height: '150px'
                        }
                      }}
                      onEnd={handleFirmaChange}
                    />
                    <div className="text-end mt-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={limpiarFirma}
                      >
                        Limpiar Firma
                      </Button>
                    </div>
                  </div>
                </Form.Group>

                {/* Botones */}
                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleRecibirEquipos}
                    disabled={guardando || equiposSeleccionados.length === 0}
                  >
                    {guardando ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Registrando recepción...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Confirmar Recepción
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Lista de equipos */}
        <Col lg={7}>
          {/* Equipos pendientes */}
          <Card className="shadow-sm mb-3 w-100">
            <Card.Header className="bg-warning text-dark w-100">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Equipos Pendientes de Devolución</h5>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={handleSeleccionarTodos}
                  disabled={equiposPendientes.length === 0}
                >
                  {equiposSeleccionados.length === equiposPendientes.length
                    ? 'Deseleccionar Todos'
                    : 'Seleccionar Todos'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0 w-100">
              {equiposPendientes.length === 0 ? (
                <Alert variant="info" className="m-3">
                  Todos los equipos ya han sido devueltos
                </Alert>
              ) : (
                <Table striped hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th>Código</th>
                      <th>Descripción</th>
                      <th>Serie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equiposPendientes.map((eq) => {
                      const patronId = eq.patron._id || eq.patron;
                      return (
                        <tr
                          key={patronId}
                          className={
                            equiposSeleccionados.includes(patronId)
                              ? 'table-success'
                              : ''
                          }
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSeleccionEquipo(patronId)}
                        >
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={equiposSeleccionados.includes(patronId)}
                              onChange={() => handleSeleccionEquipo(patronId)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td>{eq.codigo}</td>
                          <td>{eq.descripcion}</td>
                          <td>{eq.serie}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Equipos ya devueltos */}
          {equiposDevueltos.length > 0 && (
            <Card className="shadow-sm mb-4 w-100">
              <Card.Header className="bg-success text-white w-100">
                <h5 className="mb-0">Equipos Ya Devueltos</h5>
              </Card.Header>
              <Card.Body className="p-0">
                <Table className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Código</th>
                      <th>Descripción</th>
                      <th>Serie</th>
                      <th>Fecha Devolución</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equiposDevueltos.map((eq, index) => (
                      <tr key={index}>
                        <td>{eq.codigo}</td>
                        <td>{eq.descripcion}</td>
                        <td>{eq.serie}</td>
                        <td>
                          {eq.fechaDevolucion
                            ? new Date(eq.fechaDevolucion).toLocaleDateString()
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default RecibirEquiposPrestamos;